/**
 * cvp-pagination-enhance.js -- adds Tiaracle's "You have viewed X of Y designs" line, a progress
 * bar, and a "Back to top" link around Horizon's OWN numbered pagination (snippets/
 * pagination-controls.liquid), without touching that Liquid file (R06.11: drive Horizon's
 * mechanism, don't rebuild it).
 *
 * WHY THIS IS A SCRIPT AND NOT A LIQUID SNIPPET THAT RENDERS THE COUNTS SERVER-SIDE: the numbers
 * (page, total, per-page) live inside sections/cvp-collection-grid.liquid and
 * sections/cvp-search-grid.liquid, which Horizon's facets.js re-fetches and morphs on every
 * filter click WITHOUT a full page reload (assets/facets.js + the section-rendering API). A
 * server-rendered "viewed" line in a *different*, unmorphed section (the header band) would go
 * stale the moment a filter changes the count -- the exact kind of patch R12.1 forbids. Reading
 * the numbers back out of the DOM that Horizon itself just morphed, and re-running on every
 * mutation of the results list, is the root-cause-correct fix: it can never disagree with what
 * is actually on screen.
 *
 * PRODUCTS_PER_PAGE is a BUILD-TIME template setting (products_per_page: 20 on both
 * sections/cvp-collection-grid.liquid and sections/cvp-search-grid.liquid), not live store state
 * (R00.5 governs catalog/theme STATE -- which theme is live, how many products exist -- not a
 * per-page page-size the build itself chose and can change only by editing this file and the
 * matching template setting together).
 */
(function () {
  var PRODUCTS_PER_PAGE = 20;
  var injecting = false;

  /** @param {Element} root */
  function enhance(root) {
    if (injecting) return;
    var lists = root.querySelectorAll ? root.querySelectorAll('results-list') : [];
    if (root.matches && root.matches('results-list')) lists = [root];
    if (!lists.length) lists = document.querySelectorAll('results-list');

    lists.forEach(function (list) {
      var pagination = list.querySelector('.pagination');
      // Remove any stale enhancement this list is carrying (a prior render, or an empty result).
      // .cvp-pgn__head wraps BOTH the viewed line and the progress bar in one element (see below):
      // removing that one wrapper, instead of removing .cvp-pgn__meta and .cvp-pgn__bar
      // separately, is what used to leave a second bar behind on every subsequent filter click --
      // this line was missing .cvp-pgn__bar entirely, so the old bar was never cleaned up.
      list.querySelectorAll(':scope .cvp-pgn__head, :scope .cvp-pgn__top').forEach(function (n) {
        n.remove();
      });
      if (!pagination) return;

      /* a section may state its own page size (data-per-page, the collection page v2 uses 24);
         without it the build-time default below applies (the search page) */
      var perNode = list.querySelector('[data-per-page]');
      var perPage = perNode ? parseInt(perNode.getAttribute('data-per-page'), 10) || PRODUCTS_PER_PAGE : PRODUCTS_PER_PAGE;
      var countAttrNode = list.querySelector('[data-results-count]');
      var total = countAttrNode ? parseInt(countAttrNode.getAttribute('data-results-count'), 10) : NaN;
      var currentPage = parseInt(pagination.getAttribute('data-current_page'), 10) || 1;
      if (!total || isNaN(total)) {
        // fall back to the grid's own last-page marker times per-page as a floor, never blank
        var grid = list.querySelector('.product-grid[data-last-page]');
        var lastPage = grid ? parseInt(grid.getAttribute('data-last-page'), 10) : 0;
        total = lastPage ? lastPage * perPage : 0;
      }
      if (!total) return;

      var viewed = Math.min(currentPage * perPage, total);
      var pct = Math.round((viewed / total) * 1000) / 10;

      var metaTemplate =
        total === 1
          ? window.cvpPaginationI18n && window.cvpPaginationI18n.viewedOne
          : window.cvpPaginationI18n && window.cvpPaginationI18n.viewedOther;
      if (!metaTemplate) metaTemplate = 'You have viewed {{ viewed }} of {{ total }} designs';
      var metaText = metaTemplate.replace('{{ viewed }}', viewed).replace('{{ total }}', total);

      var meta = document.createElement('p');
      meta.className = 'cvp-pgn__meta';
      meta.textContent = metaText;

      var bar = document.createElement('div');
      bar.className = 'cvp-pgn__bar';
      var barFill = document.createElement('span');
      barFill.style.width = pct + '%';
      bar.appendChild(barFill);

      // ONE wrapper, built off-DOM and inserted once: two separate
      // `pagination.insertAdjacentElement('beforebegin', X)` calls insert each X immediately
      // before `pagination` as it stands at THAT moment, so inserting bar then meta actually
      // leaves the DOM order [bar, meta, pagination] -- backwards, and the reason the bar
      // rendered flush against the grid with no top margin (only .cvp-pgn__meta carried one).
      // Tiaracle's order is the viewed line, then the bar, then Horizon's own numbers.
      var head = document.createElement('div');
      head.className = 'cvp-pgn__head';
      head.appendChild(meta);
      head.appendChild(bar);

      injecting = true;
      pagination.insertAdjacentElement('beforebegin', head);

      /* Back to top lives in the footer since 2026-09-28 (footer v2); not added here any more. */
      injecting = false;
    });
  }

  function run() {
    enhance(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  var debounceId;
  var observer = new MutationObserver(function () {
    if (injecting) return;
    clearTimeout(debounceId);
    debounceId = setTimeout(run, 30);
  });
  document.querySelectorAll('results-list').forEach(function (list) {
    observer.observe(list, { childList: true, subtree: true });
  });
  // results-list elements on a page that loads after this script (design-mode section reorder,
  // rare) -- a light-weight body-level watch that only re-attaches the observer, never re-runs
  // enhance() itself outside the debounce above.
  new MutationObserver(function () {
    document.querySelectorAll('results-list').forEach(function (list) {
      observer.observe(list, { childList: true, subtree: true });
    });
  }).observe(document.body, { childList: true, subtree: false });
})();
